import React from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { toggleExploreAside } from 'actions/ui';
import { Loading, SystemText, Tabs } from '@salesperformanceint/e4shared';
import ToDo from './components/toDo';
import Completed from './components/completed';
import Resources from './components/resources';
import posthog from 'posthog-js';
import { apiGet } from 'services';

export const ExploreMenu = ({ scrollPosition }) => {
    const dispatch = useDispatch();
    const exploreMenu = React.useRef(null);
    const closeButton = React.useRef(null);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({ objectives: [], resourcse: [] });
    const [tab, setTab] = React.useState('toDo');
    const [minScroll, setMinScroll] = React.useState(scrollPosition);
    const { user } = useSelector(state => state.user);
    const { profileId, competencyId, competency } = useSelector(state => state.ui.exploreAsideOpen);

    const st = (path, ...rest) => SystemText(`PerformanceExplorer/${path}`, ...rest);
    const text = {
        tabs: {
            toDo: st('Tabs/ToDo'),
            completed: st('Tabs/Completed'),
            resources: st('Tabs/Resources'),
        },
        noResults: {
            toDo: st('NoResults/ToDo'),
            completed: st('NoResults/Completed'),
            resources: st('NoResults/Resources'),
        },
        buttons: {
            close: st('Buttons/Close'),
            viewAllObjectives: st('Buttons/ViewAllObjectives'),
        },
        card: {
            statuses: {
                onTime: st('Card/Status/OnTime'),
                late: st('Card/Status/Late'),
                overdue: st('Card/Status/Overdue'),
                outstanding: st('Card/Status/Outstanding'),
                completed: st('Card/Status/Completed'),
                notStarted: st('Card/Status/NotStarted'),
                inProgress: st('Card/Status/InProgress'),
            },
            due: st('Card/Due'),
            completed: st('Card/Completed'),
        },
    };

    React.useEffect(() => {
        setLoading(true);
        setMinScroll(999999);
        apiGet(
            `search?profile=${profileId}&competencyId=${competencyId}&pagesize=-1&types=TASK,LMS,RICHARDSON,QUESTION`,
            user.guid,
            response => {
                setData(response.results);
                setLoading(false);
                if (scrollPosition <= minScroll) {
                    const extra = scrollPosition > 30 ? -30 : 0;
                    exploreMenu.current.style.top = `${scrollPosition + extra}px`;
                }
            }
        );
    }, [profileId, competencyId]);

    React.useEffect(() => {
        if (exploreMenu.current) {
            const extra = scrollPosition > 30 ? -30 : 0;
            if (scrollPosition <= minScroll) {
                exploreMenu.current.style.top = `${scrollPosition + extra}px`;
            }

            scrollPosition < minScroll && setMinScroll(scrollPosition);
        }
    }, [profileId, competencyId, exploreMenu.current, scrollPosition]);

    React.useEffect(() => {
        const keyUp = e => {
            if (e.key === 'Escape') dispatch(toggleExploreAside(null));
        };

        document.addEventListener('keydown', keyUp, false);

        return () => {
            document.removeEventListener('keydown', keyUp, false);
        };
    }, []);

    React.useEffect(() => {
        setTimeout(() => {
            closeButton.current?.focus();
        }, 500);
    }, [closeButton.current]);

    if (loading)
        return (
            <div className="mt20">
                <Loading />
            </div>
        );

    let tabs = ['toDo', 'completed', 'resources'];

    if (!user.subDomain) {
        tabs = ['toDo', 'completed'];
    }

    return (
        <div className="p30 explore-aside" ref={exploreMenu} data-testid="explore-menu">
            <div className="flex flex-center mt10 mb20">
                <h3 className="flex-grow">{competency}</h3>
                <button
                    ref={closeButton}
                    data-testid="button-explore-close"
                    className="btn btn-link"
                    aria-label={text.buttons.close}
                    onClick={() => dispatch(toggleExploreAside(null))}
                >
                    <i className="fa fa-times-circle" />
                </button>
            </div>
            <Tabs
                style="clean"
                tab={tab}
                setTab={tab => {
                    setTab(tab);
                    posthog.capture('explore tab clicked', { $tab: tab });
                }}
                tabs={tabs}
                tabNames={{ toDo: text.tabs.toDo, completed: text.tabs.completed, resources: text.tabs.resources }}
            >
                <div className="mt20">
                    {tab === 'toDo' && <ToDo text={text} objectives={data.filter(o => !o.date_Completed)} />}
                    {tab === 'completed' && <Completed text={text} objectives={data.filter(o => o.date_Completed)} />}
                    {tab === 'resources' && (
                        <Resources
                            text={text}
                            resources={data.filter(x => x.type === 'richardson' || x.type === 'question')}
                        />
                    )}
                </div>
            </Tabs>
        </div>
    );
};

export default ExploreMenu;
